import React, { useState, useEffect } from 'react';
import { getEmployees, updateEmployee } from '../api/tailorApi';
import Layout from '../components/Layout';

// --- Helper: Data Augmentation ---
const augmentEmployeeData = (apiData) => {
    if (!Array.isArray(apiData)) {
        console.error("API did not return an array for employees. Using mock data.");

        apiData = [
            { id: "1", name: "Rajesh Kumar", employee_id: "EMP-001", phone: "9876512340", role: "Master Tailor", status: "Active", joining_date: "2023-01-15", monthly_salary: "45000", department: "Stitching" },
            { id: "2", name: "Suresh Patel", employee_id: "EMP-002", phone: "9876512341", role: "Assistant", status: "Active", joining_date: "2024-03-10", monthly_salary: "20000", department: "Stitching" },
            { id: "3", name: "Priya Raj", employee_id: "EMP-003", phone: "9876512342", role: "HR Manager", status: "On Leave", joining_date: "2024-07-01", monthly_salary: "65000", department: "Human Resources" },
        ];
    }

    return apiData.map(employee => ({
        ...employee,
        id: employee.id.toString(),
        employee_id: employee.employee_id || `EMP-${employee.id}`,
        department: employee.department || 'General',
        display_salary: employee.monthly_salary ? parseFloat(employee.monthly_salary).toLocaleString('en-IN') : 'N/A',
        monthly_salary: employee.monthly_salary || '0',
        salary_type: employee.salary_type || 'Monthly',
        activeOrders: Math.floor(Math.random() * 6),
        completedOrders: Math.floor(Math.random() * 100) + 50,
        specialization: employee.role.includes('Tailor') ? "Men's Wear" : 'All Types',
        email: `${employee.name.toLowerCase().replace(/\s/g, '.')}@tailorshop.com`,
    }));
};

// --- EmployeeEditCard (with local error handling) ---
const EmployeeEditCard = ({ employee, onSave, onCancel }) => {
    const [formData, setFormData] = useState(employee);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSaving(true);
        try {
            await onSave(formData);
        } catch (err) {
            console.error("Error saving employee:", err);
            setError(err.message || 'Failed to save changes due to an unexpected error.');
        } finally {
            setIsSaving(false);
        }
    };

    const inputClasses = "w-full p-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500";
    const labelClasses = "block text-sm font-medium text-gray-700";

    return (
        <div className="bg-white p-8 shadow-2xl rounded-xl border border-gray-200 max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Employee: {employee.name}</h2>
            {error && <div className="p-3 mb-4 bg-red-100 text-red-700 rounded-md text-sm border border-red-300">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClasses}>Name</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Employee ID</label>
                        <input type="text" name="employee_id" value={formData.employee_id} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Phone</label>
                        <input type="text" name="phone" value={formData.phone} onChange={handleChange} required className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Role</label>
                        <input type="text" name="role" value={formData.role} onChange={handleChange} className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Department</label>
                        <input type="text" name="department" value={formData.department} onChange={handleChange} className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className={inputClasses}>
                            <option>Active</option>
                            <option>On Leave</option>
                            <option>Inactive</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClasses}>Monthly Salary (₹)</label>
                        <input type="number" name="monthly_salary" value={formData.monthly_salary} onChange={handleChange} className={inputClasses} step="any" />
                    </div>
                    <div>
                        <label className={labelClasses}>Joining Date</label>
                        <input type="date" name="joining_date" value={formData.joining_date} onChange={handleChange} className={inputClasses} />
                    </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t mt-4">
                    <button type="button" onClick={onCancel} disabled={isSaving} className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-md text-sm hover:bg-gray-100 disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-4 rounded-md text-sm disabled:opacity-50">
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// --- EmployeeViewDetailsCard ---
const EmployeeViewDetailsCard = ({ employee, onBack }) => {
    return (
        <div className="bg-white p-8 shadow-2xl rounded-xl border border-gray-200 max-w-xl mx-auto">
            <div className="flex justify-between items-start mb-6 border-b pb-4">
                <h2 className="text-2xl font-bold text-gray-900">Details for {employee.name}</h2>
                <button onClick={onBack} className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md text-sm">
                    Back to List
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
                <p><strong>Employee ID:</strong> {employee.employee_id}</p>
                <p><strong>Status:</strong> <span className={`font-medium ${employee.status === 'Active' ? 'text-green-600' : 'text-yellow-600'}`}>{employee.status}</span></p>
                <p><strong>Role:</strong> {employee.role}</p>
                <p><strong>Department:</strong> {employee.department}</p>
                <p><strong>Phone:</strong> {employee.phone}</p>
                <p><strong>Email:</strong> {employee.email}</p>
                <p><strong>Joining Date:</strong> {employee.joining_date}</p>
                <p><strong>Specialization:</strong> {employee.specialization}</p>
                <p className="sm:col-span-2"><strong>Salary:</strong> ₹{employee.display_salary} ({employee.salary_type})</p>

                <h3 className="text-md font-semibold text-gray-800 mt-4 sm:col-span-2">Order Statistics</h3>
                <div className="border border-gray-100 p-3 rounded-lg bg-gray-50">
                    <p className="text-sm text-gray-600">Active Orders</p>
                    <p className="text-xl font-bold text-gray-900">{employee.activeOrders}</p>
                </div>
                <div className="border border-gray-100 p-3 rounded-lg bg-gray-50">
                    <p className="text-sm text-gray-600">Completed Orders</p>
                    <p className="text-xl font-bold text-green-600">{employee.completedOrders}</p>
                </div>
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end">
                <button onClick={onBack} className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-md text-sm hover:bg-gray-100">
                    Close Details
                </button>
            </div>
        </div>
    );
};

// --- EmployeeCard ---
const EmployeeCard = ({ employee, onEdit, onViewDetails }) => {
    const getInitials = (name) => {
        const parts = name.split(' ');
        if (parts.length > 1) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name[0].toUpperCase();
    };

    const statusColor = employee.status === 'Active' ? 'bg-green-100 text-green-800' : employee.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

    return (
        <div className="bg-white p-6 shadow-md rounded-lg border border-gray-200">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        {getInitials(employee.name)}
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-gray-900">{employee.name}</p>
                        <p className="text-sm text-gray-500">{employee.employee_id}</p>
                    </div>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusColor}`}>
                    {employee.status}
                </span>
            </div>

            <div className="mt-4 space-y-1 text-sm text-gray-600 border-b pb-4 mb-4">
                <p>📞 {employee.phone}</p>
                <p>📧 {employee.email}</p>
            </div>

            <h3 className="text-md font-semibold text-gray-800 mb-2">Position Details</h3>
            <div className="grid grid-cols-2 gap-y-2 text-sm mb-4">
                <div><span className="font-medium">Role:</span> {employee.role || 'N/A'}</div>
                <div><span className="font-medium">Department:</span> {employee.department}</div>
            </div>

            <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold text-gray-600">Active Orders</span><p className="text-xl font-bold text-gray-900">{employee.activeOrders}</p></div>
                <div><span className="font-semibold text-gray-600">Completed</span><p className="text-xl font-bold text-green-600">{employee.completedOrders}</p></div>
            </div>

            <div className="border-t pt-4 mt-4 grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold text-gray-600">Salary</span><p className="text-lg font-bold text-gray-900">₹{employee.display_salary}</p></div>
                <div><span className="font-semibold text-gray-600">Type</span><p className="text-lg font-bold text-gray-900">{employee.salary_type}</p></div>
                <div className="col-span-2"><span className="font-semibold text-gray-600">Join Date:</span> {employee.joining_date}</div>
                <div className="col-span-2"><span className="font-semibold text-gray-600">Specialization:</span> {employee.specialization}</div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
                <button
                    onClick={() => onEdit(employee)}
                    className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-md text-sm hover:bg-gray-100"
                >
                    Edit
                </button>
                <button
                    onClick={() => onViewDetails(employee)}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md text-sm"
                >
                    View Details
                </button>
            </div>
        </div>
    );
};

// --- EmployeeListing (Main Component) ---
const EmployeeListing = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [mode, setMode] = useState('list'); // 'list', 'view', 'edit'

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const apiData = await getEmployees();
            setEmployees(augmentEmployeeData(apiData));
        } catch (error) {
            console.error("Failed to fetch employees:", error);
            setEmployees(augmentEmployeeData([]));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const handleEditClick = (employee) => {
        setSelectedEmployee(employee);
        setMode('edit');
    };

    const handleViewDetailsClick = (employee) => {
        setSelectedEmployee(employee);
        setMode('view');
    };

    const handleBackToList = () => {
        setSelectedEmployee(null);
        setMode('list');
    };

    const handleSaveEmployee = async (updatedData) => {
        try {
            const response = await updateEmployee(updatedData);

            if (response.success) {
                setEmployees(prevEmployees =>
                    prevEmployees.map(e => (e.id === updatedData.id ? augmentEmployeeData([updatedData])[0] : e))
                );
                handleBackToList();
                return Promise.resolve();
            } else {
                throw new Error(response.message || 'Update failed on server.');
            }
        } catch (error) {
            throw error;
        }
    };

    const filteredEmployees = employees.filter(e => {
        const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (e.employee_id && e.employee_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (e.phone && e.phone.includes(searchTerm));

        const matchesStatus = filterStatus === 'All' || e.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const statusButtons = ['All', 'Active', 'On Leave', 'Inactive'];

    let content;
    if (mode === 'edit' && selectedEmployee) {
        content = (
            <EmployeeEditCard
                employee={selectedEmployee}
                onSave={handleSaveEmployee}
                onCancel={handleBackToList}
            />
        );
    } else if (mode === 'view' && selectedEmployee) {
        content = (
            <EmployeeViewDetailsCard
                employee={selectedEmployee}
                onBack={handleBackToList}
            />
        );
    } else { // mode === 'list'
        content = (
            <>
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0">
                    <input
                        type="text"
                        placeholder="Search by name, employee ID, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full md:w-1/2 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    <div className="flex space-x-2">
                        {statusButtons.map(status => (
                            <button
                                key={status}
                                onClick={() => setFilterStatus(status)}
                                className={`py-2 px-4 rounded-lg font-medium text-sm transition duration-150 ${filterStatus === status
                                    ? 'bg-gray-800 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {loading && <div className="text-center text-xl p-8">Loading Employees...</div>}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {!loading && filteredEmployees.map(employee => (
                        <EmployeeCard
                            key={employee.id}
                            employee={employee}
                            onEdit={handleEditClick}
                            onViewDetails={handleViewDetailsClick}
                        />
                    ))}
                </div>

                {!loading && filteredEmployees.length === 0 && (
                    <div className="text-center text-gray-500 p-8">No employees found matching your criteria.</div>
                )}
            </>
        );
    }

    return (

        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {mode === 'list' ? 'Employee Listing' : mode === 'edit' ? 'Edit Employee' : 'Employee Details'}
            </h1>
            <p className="text-sm text-gray-500 mb-6">
                {mode === 'list' ? 'View all employees and their details' : 'Modify or view selected employee data'}
            </p>

            {content}

        </div>

    );
};

export default EmployeeListing;